import { isEmpty } from 'lodash'
import { ReactElement } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { CopyToClipboard, CopyToClipboardWrapper } from '@10xLabs/react-web/components/CopyToClipboard'
import { FormattedDate } from '@10xLabs/react-web/components/FormattedDate'
import { FormattedTimeByDate } from '@10xLabs/react-web/components/FormattedTimeByDate'
import { TableContainer, Table, Thead, Th, Tbody, Tr, Td } from 'components/v2/Table'
import { SecondaryInfo } from 'features/App/components/SecondaryInfo'
import { BookingService } from 'features/Bookings/components/Service'
import { TicketInsuranceStatus } from 'features/Bookings/components/TicketInsuranceStatus'
import { ConciliationStatus } from 'features/Inventories/components/ConciliationStatus'
import { CONCILIATION_STATUS } from 'features/Inventories/components/ConciliationStatus/constants'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { canWrite } from 'features/Users/utils'
import { TicketInsuranceActions } from './Actions'
import { useLogin } from 'features/Login/hooks'
import { GTBookingV2Trip, GTTicketInsurance } from 'gql/types.generated'

export const TicketsInsuranceList = ({
  ticketsInsurance = []
}: {
  ticketsInsurance: GTTicketInsurance[]
}): ReactElement => {
  const intl = useIntl()
  const login = useLogin()

  return (
    <TableContainer overflow>
      <Table marginBottom>
        <Thead>
          <Tr>
            {canWrite(FEATURES.BOOKING, login.accessToken.policies) && <Th />}
            <Th>
              <FormattedMessage id="ticketsInsurance.list.reservation" defaultMessage="Reservation" />
            </Th>
            <Th>
              <FormattedMessage id="ticketsInsurance.list.ticket" defaultMessage="Ticket id" />
            </Th>
            <Th>
              <FormattedMessage id="ticketsInsurance.list.passenger" defaultMessage="Passenger" />
            </Th>
            <Th>
              <FormattedMessage id="ticketsInsurance.list.status" defaultMessage="Status" />
            </Th>
            <Th>
              <FormattedMessage id="ticketsInsurance.list.service" defaultMessage="Service" />
            </Th>
            <Th>
              <FormattedMessage id="ticketsInsurance.list.policyData" defaultMessage="Policy data" />
            </Th>
            <Th>
              <FormattedMessage id="ticketsInsurance.list.conflict" defaultMessage="Conflict" />
            </Th>
            <Th>
              <FormattedMessage id="ticketsInsurance.list.paymentDate" defaultMessage="Payment" />
            </Th>
            <Th>
              <FormattedMessage id="ticketsInsurance.list.certificate" defaultMessage="Certificate" />
            </Th>
            <Th>
              <FormattedMessage id="ticketsInsurance.list.insurancePolicy" defaultMessage="Policy" />
            </Th>
          </Tr>
        </Thead>

        <Tbody>
          {ticketsInsurance.map((ticketInsurance): TSFixMe => {
            const { insuranceCoverage } = ticketInsurance.insurance

            return (
              <Tr key={ticketInsurance.id}>
                {canWrite(FEATURES.BOOKING, login.accessToken.policies) && (
                  <Td>
                    <TicketInsuranceActions ticketInsurance={ticketInsurance} />
                  </Td>
                )}
                <Td>
                  <CopyToClipboardWrapper>
                    <div>{ticketInsurance.bookingID.substring(0, 8)}</div>
                    <CopyToClipboard str={ticketInsurance.bookingID} />
                  </CopyToClipboardWrapper>
                </Td>
                <Td>
                  <CopyToClipboardWrapper>
                    <div>{ticketInsurance.id.substring(0, 8)}</div>
                    <CopyToClipboard str={ticketInsurance.id} />
                  </CopyToClipboardWrapper>
                </Td>
                <Td>
                  {ticketInsurance.passenger.firstName} {ticketInsurance.passenger.lastName}
                </Td>
                <Td>
                  <TicketInsuranceStatus status={ticketInsurance.insurance.status} />
                </Td>
                <Td>
                  <BookingService
                    departureTrips={[
                      {
                        ...ticketInsurance.trip
                      } as GTBookingV2Trip
                    ]}
                    showDepartureTrips
                  />
                </Td>
                <Td>
                  {insuranceCoverage ? (
                    <>
                      <div>
                        <FormattedDate
                          intl={intl}
                          value={insuranceCoverage.departureAt}
                          month="2-digit"
                          day="2-digit"
                          year="numeric"
                        />
                        <SecondaryInfo>
                          <FormattedTimeByDate intl={intl} date={insuranceCoverage.departureAt} />
                        </SecondaryInfo>
                      </div>
                      {!isEmpty(insuranceCoverage.passenger) && (
                        <div>
                          {insuranceCoverage.passenger.firstName} {insuranceCoverage.passenger.lastName}
                        </div>
                      )}
                    </>
                  ) : (
                    '-'
                  )}
                </Td>
                <Td>
                  {insuranceCoverage ? (
                    <>
                      {ticketInsurance.hasConflict ? (
                        <ConciliationStatus status={CONCILIATION_STATUS.CONFLICTING} />
                      ) : (
                        <ConciliationStatus status={CONCILIATION_STATUS.COMPLETED} />
                      )}
                    </>
                  ) : (
                    '-'
                  )}
                </Td>
                <Td>
                  {ticketInsurance.insurance.confirmedAt ? (
                    <>
                      <FormattedDate
                        intl={intl}
                        value={ticketInsurance.insurance.confirmedAt}
                        month="2-digit"
                        day="2-digit"
                        year="numeric"
                      />
                      <SecondaryInfo>
                        <FormattedTimeByDate intl={intl} date={ticketInsurance.insurance.confirmedAt} />
                      </SecondaryInfo>
                    </>
                  ) : (
                    '-'
                  )}
                </Td>
                <Td>
                  {ticketInsurance.insurance.certificateID ? (
                    <CopyToClipboardWrapper>
                      <div>{ticketInsurance.insurance.certificateID.substring(0, 8)}</div>
                      <CopyToClipboard str={ticketInsurance.insurance.certificateID} />
                    </CopyToClipboardWrapper>
                  ) : (
                    '-'
                  )}
                </Td>
                <Td>
                  {ticketInsurance.insurance.externalID ? (
                    <CopyToClipboardWrapper>
                      <div>{ticketInsurance.insurance.externalID.substring(0, 8)}</div>
                      <CopyToClipboard str={ticketInsurance.insurance.externalID} />
                    </CopyToClipboardWrapper>
                  ) : (
                    '-'
                  )}
                </Td>
              </Tr>
            )
          })}
        </Tbody>
      </Table>
    </TableContainer>
  )
}
